/**
 * Привязки Прораба к позициям объёма РД, когда сами позиции меняются (П-4:
 * числа считает сервер, здесь — отбор и подписи):
 * — отчёт перепривязки ревизии после «Извлечь объёмы» или переиндексации
 *   (`revision.metadata.volumes_relink`): сколько ссылок отвязано и сколько
 *   позиций с привязками модель не назвала;
 * — перенос привязок на новую ревизию РД (`/agents/pto/projects/{id}/rd-carry-over`):
 *   позиции заменённой ревизии с элементами, фактами и строками ОЖР и их
 *   продолжения в действующей ревизии того же документа.
 *
 * Модуль без импортов значений: Node-тесты (`logic.test.mjs`) снимают типы без
 * сборщика и путей-алиасов.
 */
import type {
  PtoRdCarryOverApplied,
  PtoRdCarryOverItem,
  PtoRdCarryOverPair,
} from '@shared/api/types';

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/** Итог одной замены позиций ревизии — по набору источников позиций. */
export interface VolumesRelinkStat {
  /** Набор источников: `rd` (текст, «Извлечь объёмы») или `rd_table,rd_geometry` (таблицы). */
  roles: string;
  /** Когда была замена (ISO); null — не записано. */
  at: string | null;
  /** Ссылок без однозначного двойника — отвязаны вместе со старой позицией. */
  detachedRefs: number;
  /** Из них элементов конструктива Прораба. */
  detachedElements: number;
  /** Позиций с привязками, которые модель не назвала: оставлены на проверку. */
  keptReferenced: number;
}

/** Неотрицательное целое из JSON; иное — 0. */
function count(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
}

/**
 * Отчёт перепривязки из `metadata` ревизии: `volumes_relink` — объект «набор
 * источников → итог последней замены». Нет отчёта или формат чужой — пусто.
 * Списки элементов и оставленных позиций сервер обрезает до 50 — число берём из
 * `*_total`.
 */
export function volumesRelinkStats(metadata: unknown): VolumesRelinkStat[] {
  if (typeof metadata !== 'object' || metadata === null) return [];
  const relink = (metadata as Record<string, unknown>).volumes_relink;
  if (typeof relink !== 'object' || relink === null || Array.isArray(relink)) return [];
  return Object.entries(relink as Record<string, unknown>)
    .filter(([, v]) => typeof v === 'object' && v !== null && !Array.isArray(v))
    .map(([roles, v]) => {
      const r = v as Record<string, unknown>;
      const listed = (key: string) => (Array.isArray(r[key]) ? (r[key] as unknown[]).length : 0);
      return {
        roles,
        at: typeof r.at === 'string' ? r.at : null,
        detachedRefs: count(r.detached_refs),
        detachedElements: Math.max(count(r.detached_elements_total), listed('detached_elements')),
        keptReferenced: Math.max(count(r.kept_referenced_total), listed('kept_referenced')),
      };
    })
    .sort((a, b) => a.roles.localeCompare(b.roles));
}

/** Источник позиций словами: текст РД или таблицы спецификаций. */
export function relinkSourceLabel(roles: string): string {
  const set = roles.split(',').map((s) => s.trim());
  if (set.includes('rd_table') || set.includes('rd_geometry')) return 'таблицы спецификаций';
  if (set.includes('rd')) return 'текст РД';
  return roles;
}

/** Предупреждение ревизии о привязках, которые замена позиций не сохранила. */
export interface RelinkWarning {
  tone: 'danger' | 'warning';
  text: string;
  /** Что делать. */
  hint: string;
  /** Набор источников из отчёта (ключ `volumes_relink`). */
  roles: string;
  /** Откуда позиции: «текст РД» | «таблицы спецификаций». */
  source: string;
  /** Когда была замена (ISO) — подпись даты делает экран. */
  at: string | null;
}

/**
 * Предупреждения ревизии после замены позиций (переизвлечение, переиндексация):
 * ссылки без однозначного двойника отвязаны — элементы Прораба остались без
 * позиции, сверки их фактов нет, пока элемент не привязать заново; позиции с
 * привязками, которые модель в этот раз не назвала, оставлены с пометкой «нужна
 * проверка» — их сверяют с листом. Всё перенеслось — предупреждений нет.
 */
export function volumesRelinkWarnings(metadata: unknown): RelinkWarning[] {
  const out: RelinkWarning[] = [];
  for (const s of volumesRelinkStats(metadata)) {
    const source = relinkSourceLabel(s.roles);
    if (s.detachedRefs > 0) {
      out.push({
        tone: 'danger',
        text:
          `Отвязано от РД ссылок: ${s.detachedRefs}` +
          (s.detachedElements > 0 ? ` (элементов конструктива: ${s.detachedElements})` : '') +
          ' — привяжите заново',
        hint:
          'Однозначного двойника прежней позиции в новом извлечении не нашлось — привязка ' +
          'снята. Элементы Прораба привяжите к позиции на странице площадки: «Конструктив» → ' +
          '«Привязать к позиции РД…» — факты элемента получат ту же позицию.',
        roles: s.roles,
        source,
        at: s.at,
      });
    }
    if (s.keptReferenced > 0) {
      out.push({
        tone: 'warning',
        text:
          `Модель не назвала позиций с привязками: ${s.keptReferenced} — оставлены на ` +
          'проверку',
        hint:
          'Позиции остались с прежними привязками и пометкой «нужна проверка»: сверьте их с ' +
          'листом в «Объёмах» ревизии (в выборе позиции элемента они помечены «нет в ' +
          'последнем извлечении РД»).',
        roles: s.roles,
        source,
        at: s.at,
      });
    }
  }
  return out;
}

/**
 * Метка комплекта «в производство: дата, подписант» (ОСНОВА.Инженер №6): штамп
 * заказчика «В ПРОИЗВОДСТВО РАБОТ» на листах ревизии (`metadata.afc_stamp`,
 * пишет индексация). Штампа нет — null.
 */
export function afcStampLabel(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const stamp = (metadata as Record<string, unknown>).afc_stamp;
  if (!stamp || typeof stamp !== 'object') return null;
  const s = stamp as Record<string, unknown>;
  const parts = [s.date, s.signer]
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .map((v) => v.trim());
  return parts.length > 0 ? `в производство: ${parts.join(', ')}` : 'в производство';
}

/** Статус продолжения позиции в действующей ревизии: подпись, тон, пояснение. */
export interface CarryOverStatusView {
  label: string;
  tone: Tone;
  hint: string;
}

/** `same` | `quantity_changed` | `paraphrase` | `not_found` | `ambiguous` → словами. */
export function carryOverStatusView(status: string): CarryOverStatusView {
  switch (status) {
    case 'same':
      return {
        label: 'та же позиция',
        tone: 'success',
        hint: 'В действующей ревизии та же позиция с тем же количеством.',
      };
    case 'quantity_changed':
      return {
        label: 'количество изменилось',
        tone: 'warning',
        hint:
          'В действующей ревизии другое количество: после переноса сверка фактов и ' +
          'накопительный контроль идут по новому.',
      };
    case 'paraphrase':
      return {
        label: 'наименование пересказано',
        tone: 'info',
        hint:
          'Наименование записано иначе (слова одного — часть другого), количество и единица ' +
          'совпадают.',
      };
    case 'not_found':
      return {
        label: 'нет в действующей ревизии',
        tone: 'danger',
        hint:
          'Продолжения позиции в новой ревизии не нашлось — автоматически не переносится. ' +
          'Элемент привяжите к позиции на странице площадки («Сменить позицию РД…»).',
      };
    case 'ambiguous':
      return {
        label: 'неоднозначно',
        tone: 'danger',
        hint:
          'Подходят несколько позиций новой ревизии — выберите позицию элемента вручную на ' +
          'странице площадки («Сменить позицию РД…»).',
      };
    default:
      return { label: status, tone: 'neutral', hint: '' };
  }
}

/** Позицию можно перенести: продолжение в действующей ревизии однозначно. */
export function carryOverMovable(i: Pick<PtoRdCarryOverItem, 'new_position_id'>): boolean {
  return !!i.new_position_id;
}

/**
 * Пары переноса — однозначные продолжения, кроме снятых инженером. На сервер
 * уходят именно они, а не «все однозначные на момент нажатия»: если план успел
 * измениться (новая ревизия, переизвлечение), перенесётся только то, что
 * инженер видел, а устаревшую пару сервер отклонит.
 */
export function carryOverPairs(
  items: readonly PtoRdCarryOverItem[],
  excluded: ReadonlySet<string>,
): PtoRdCarryOverPair[] {
  const out: PtoRdCarryOverPair[] = [];
  for (const i of items) {
    if (i.new_position_id && !excluded.has(i.old_position_id)) {
      out.push({ old_position_id: i.old_position_id, new_position_id: i.new_position_id });
    }
  }
  return out;
}

/** Что уйдёт на новые позиции при переносе отмеченных. */
export interface CarryOverTotals {
  positions: number;
  elements: number;
  facts: number;
  factsAccepted: number;
  journal: number;
}

/** Итог отмеченных к переносу (сумма по позициям, числа — с сервера). */
export function carryOverTotals(
  items: readonly PtoRdCarryOverItem[],
  excluded: ReadonlySet<string>,
): CarryOverTotals {
  const t: CarryOverTotals = { positions: 0, elements: 0, facts: 0, factsAccepted: 0, journal: 0 };
  for (const i of items) {
    if (!i.new_position_id || excluded.has(i.old_position_id)) continue;
    t.positions += 1;
    t.elements += i.elements;
    t.facts += i.facts;
    t.factsAccepted += i.facts_accepted;
    t.journal += i.journal;
  }
  return t;
}

/** Количество позиции: «98 шт»; null — «без количества». */
function qtyText(q: number | null, unit: string | null): string {
  if (q === null || !Number.isFinite(q)) return 'без количества';
  const n = q.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
  return unit?.trim() ? `${n} ${unit.trim()}` : n;
}

/** Количество «было → стало»; не изменилось — одним числом; продолжения нет — только «было». */
export function carryOverQtyText(
  i: Pick<PtoRdCarryOverItem, 'old_quantity' | 'new_quantity' | 'new_position_id' | 'unit'>,
): string {
  const before = qtyText(i.old_quantity, i.unit);
  if (!i.new_position_id) return before;
  const after = qtyText(i.new_quantity, i.unit);
  return before === after ? before : `${before} → ${after}`;
}

/** Итог переноса словами (для сообщения после POST). */
export function carryOverAppliedText(a: PtoRdCarryOverApplied): string {
  return (
    `Привязки перенесены на действующую ревизию: позиций ${a.pairs}, элементов ` +
    `конструктива ${a.elements}, фактов объёма ${a.facts} (из них принятых ` +
    `${a.facts_accepted}), строк ОЖР ${a.journal}.` +
    (a.open_fact_ids.length > 0
      ? ` Сверка непринятых фактов (${a.open_fact_ids.length}) пересчитана по новым позициям.`
      : '')
  );
}
